import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useSeasonal, isSeasonOn } from '../lib/useSeasonal';
import { Sun, Moon, ShoppingCart, User, Package, LogOut, Heart } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useWishlist } from '../context/WishlistContext';
import AuthModal from './AuthModal';
import OrderHistoryModal from './OrderHistoryModal';
import WishlistModal from './WishlistModal';
import HeaderSearch from './HeaderSearch';
import CurrencySwitcher from './CurrencySwitcher';
import { useNavPages } from '../lib/useNavPages';

const Header = () => {
  const { theme, themeLocked, toggleTheme, cartCount, setCartOpen } = useApp();
  const { auth, isAuthed, logout, authModalOpen, openAuth, closeAuth, authReason } = useCustomerAuth();
  const wishlist = useWishlist();
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pages = useNavPages();
  const [marquee, setMarquee] = useState(null);
  const seasonal = useSeasonal();
  const ann = isSeasonOn(seasonal) && seasonal.announcement?.enabled && seasonal.announcement.text ? [{ ...seasonal.announcement, seasonal: true }] : [];
  const msgs = [...ann, ...(marquee?.enabled ? marquee.messages || [] : [])];
  const { pathname } = useLocation();
  const headerRef = useRef(null);
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const onScroll = () => setCompact(prev => window.innerWidth < 768 && (prev ? window.scrollY > 20 : window.scrollY > 200));
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); };
  }, []);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => document.documentElement.style.setProperty('--header-h', `${el.offsetHeight}px`));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/marquee`).then(({ data }) => setMarquee(data)).catch(() => {});
  }, []);
  useEffect(() => { if (pathname === '/') document.title = 'Healsglobe Exports'; }, [pathname]);
  const navCls = ({ isActive }) => `relative px-1 py-1 text-[13px] whitespace-nowrap transition-colors after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:bg-brand after:transition-[width] after:duration-300 ${isActive ? 'text-brand font-medium after:w-full' : 'text-neutral-600 dark:text-neutral-300 hover:text-brand after:w-0 hover:after:w-full'}`;
  const nav = (testPrefix) => pages.map(p => (
    <NavLink key={p.id} to={p.to} data-testid={`${testPrefix}-${p.slug}`} className={navCls}>{p.title}</NavLink>
  ));
  return (
    <>
    <header ref={headerRef} className="sticky top-0 z-40 bg-glass border-b">
      <div className="max-w-7xl mx-auto px-6 py-3 md:py-0 md:h-16 flex flex-wrap md:flex-nowrap items-center justify-between gap-x-3 md:gap-x-6 gap-y-3">
        <Link to="/" data-testid="header-logo" className="flex items-center group shrink-0">
          <div className="text-center leading-none">
            <div className="font-serif tracking-[0.35em] text-[18px] text-neutral-900 dark:text-neutral-100 transition-colors group-hover:text-brand">HEALSGLOBE</div>
            <div className="tracking-[0.4em] text-[9px] mt-0.5 text-brand font-semibold">EXPORTS</div>
          </div>
        </Link>

        <div className="order-last w-full md:order-none md:w-auto md:flex-1 md:max-w-3xl" data-testid="header-search-wrap"><HeaderSearch /></div>

        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          <CurrencySwitcher />
          {!themeLocked && <div
            data-testid="theme-toggle"
            className="flex items-center rounded-full border border-neutral-200 dark:border-neutral-800 p-1 bg-white/60 dark:bg-neutral-900/60 backdrop-blur"
          >
            <button
              data-testid="theme-toggle-light"
              aria-label="Light mode"
              aria-pressed={theme === 'light'}
              onClick={()=> (theme==='dark' || window.innerWidth < 640) && toggleTheme()}
              className={`p-1.5 rounded-full transition-all ${theme==='light' ? 'bg-brand text-white shadow-sm' : 'hidden sm:block text-neutral-400 hover:text-neutral-600'}`}
            >
              <Sun size={15} />
            </button>
            <button
              data-testid="theme-toggle-dark"
              aria-label="Dark mode"
              aria-pressed={theme === 'dark'}
              onClick={()=> (theme==='light' || window.innerWidth < 640) && toggleTheme()}
              className={`p-1.5 rounded-full transition-all ${theme==='dark' ? 'bg-brand text-white shadow-sm' : 'hidden sm:block text-neutral-400 hover:text-neutral-600'}`}
            >
              <Moon size={15} />
            </button>
          </div>}

          {/* Customer auth */}
          {isAuthed ? (
            <div className="relative">
              <button onClick={() => setMenuOpen(v => !v)} onBlur={() => setTimeout(() => setMenuOpen(false), 150)} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-full px-3 py-1.5 text-[12.5px] text-neutral-700 dark:text-neutral-200 hover:border-brand hover:text-brand transition-colors">
                <User size={13} />
                <span className="hidden sm:inline max-w-[140px] truncate">{auth?.email}</span>
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-1 w-52 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-[#141416] shadow-lg z-10 py-1" onMouseDown={(e) => e.preventDefault()}>
                  <button data-testid="user-menu-order-history" onMouseDown={() => { setOrdersOpen(true); setMenuOpen(false); }} className="w-full text-left px-3 py-2 text-[13px] hover:bg-neutral-50 dark:hover:bg-neutral-800 inline-flex items-center gap-2"><Package size={14} /> Order history</button>
                  <button data-testid="user-menu-sign-out" onMouseDown={() => { logout(); setMenuOpen(false); }} className="w-full text-left px-3 py-2 text-[13px] hover:bg-neutral-50 dark:hover:bg-neutral-800 inline-flex items-center gap-2 text-red-600"><LogOut size={14} /> Sign out</button>
                </div>
              )}
            </div>
          ) : (
            <button data-testid="header-signin-btn" aria-label="Sign in" onClick={() => openAuth()} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-full px-3 py-1.5 text-[12.5px] text-neutral-700 dark:text-neutral-200 hover:border-brand hover:text-brand transition-colors">
              <User size={13} /> <span className="hidden sm:inline">Sign in</span>
            </button>
          )}

          <button
            data-testid="wishlist-open"
            onClick={() => wishlist?.setOpen(true)}
            className="relative p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            aria-label="Wishlist"
          >
            <Heart size={20} className="text-neutral-700 dark:text-neutral-200" />
            {wishlist?.count > 0 && (
              <span data-testid="wishlist-count" className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{wishlist.count}</span>
            )}
          </button>

          <button data-testid="cart-open" onClick={()=>setCartOpen(true)} className="relative p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors" aria-label="Cart">
            <ShoppingCart size={20} className="text-neutral-700 dark:text-neutral-200" />
            {cartCount > 0 && (
              <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-brand text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-[0_0_0_2px_hsl(var(--bg-canvas))]">{cartCount}</span>
            )}
          </button>
        </div>
      </div>

      {pages.length > 0 && !compact && (
        <div className="max-w-7xl mx-auto px-6">
          <nav data-testid="header-nav" className="overflow-x-auto scrollbar-none pb-2 md:-mt-1"><div className="flex items-center gap-6 w-max ml-auto">{nav('nav-page')}</div></nav>
        </div>
      )}

      {/* Marquee — bold emerald bar */}
      {msgs.length > 0 && !compact && (
      <div data-testid="marquee-bar" className="bg-gradient-to-r from-[hsl(var(--brand-strong))] via-[hsl(var(--brand))] to-[hsl(var(--brand-strong))] text-white overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee hover:[animation-play-state:paused] py-2 text-[12.5px] font-medium tracking-wide">
          {Array.from({length: 8}).map((_,i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-6">
              {msgs.map((m, j) => {
                const inner = m.link?.startsWith('/')
                  ? <Link to={m.link} data-testid={i === 0 ? `marquee-link-item-${j}` : undefined} className="underline underline-offset-4 decoration-white/50 hover:decoration-white">{m.text}</Link>
                  : m.link
                    ? <a href={m.link} target="_blank" rel="noopener noreferrer" data-testid={i === 0 ? `marquee-link-item-${j}` : undefined} className="underline underline-offset-4 decoration-white/50 hover:decoration-white">{m.text}</a>
                    : <span data-testid={i === 0 ? (m.seasonal ? 'marquee-seasonal-item' : `marquee-item-${j}`) : undefined} className={m.seasonal ? 'font-semibold' : ''}>{m.text}</span>;
                return <React.Fragment key={j}>{j > 0 && <span className="opacity-70">·</span>}{inner}</React.Fragment>;
              })}
            </span>
          ))}
        </div>
      </div>
      )}
    </header>
      <AuthModal open={authModalOpen} onClose={closeAuth} reason={authReason} />
      <OrderHistoryModal open={ordersOpen} onClose={() => setOrdersOpen(false)} />
      <WishlistModal />
    </>
  );
};

export default Header;
